// MorePage.tsx — "Lisää" — Valikkosivu operaattorin lisätoiminnoille
// 27.8.2026: sivut kategorioittain (käyttäjän hyväksymä jako) — aiemmin kaikki
// oli yhtenä sumppuna. Jako seuraa työpäivän vaiheita: linjalla ajaminen,
// mittaaminen/testaus, kylpyjen hoito, tavaravirta, suunnittelu, omat asiat.
// Sama ryhmittelymalli kuin johdon Lisää-sivulla (ExecMorePage).
//
// 1.9.2026: valikon SISÄLTÖ (kategoriat, järjestys, emojit, otsikot, kuvaukset
// ja polut) luetaan jaetusta shared/navigation.json:sta. Miksi: sama valikko
// piirretään myös Swift-natiivisovelluksessa, ja kahtena kovakoodattuna listana
// ne ajautuisivat erilleen heti ensimmäisestä uudesta sivusta. Tässä
// komponentissa on enää esitys ja rooliehdot — ei dataa.
import { useNavigate } from 'react-router-dom';
import navigation from '../../../shared/navigation.json';
import { useAuth } from '../api/auth';
import { FONT_FAMILY_NARROW, TOKENS, rgba } from '../styles/designTokens';
import GlassCard, { SectionTitle } from './GlassCard';

export type MenuCategory = 'linja' | 'laatu' | 'kylvyt' | 'varasto' | 'suunnittelu' | 'omat';

export const CATEGORIES: Array<{ key: MenuCategory; icon: string; title: string }> =
  navigation.categories.map(c => ({ key: c.key as MenuCategory, icon: c.icon, title: c.title }));

export interface MenuItem {
  icon: string;
  label: string;
  desc: string;
  path: string;
  cat: MenuCategory;
  /** Nostaa kohteen ryhmänsä kärkeen (vain rooliehtoisilla kohteilla). */
  first?: boolean;
}

// HUOM: OperatorShell käyttää tätä listaa alapalkin paikkojen vaihtoehtoina —
// cat-kenttä on lisätieto eikä muuta listan muotoa. Litistys säilyttää
// kategorioiden järjestyksen, joten valitsimen järjestys pysyy ennallaan.
export const MENU_ITEMS: MenuItem[] = navigation.categories.flatMap(c =>
  c.items.map(i => ({
    icon: i.icon,
    label: i.title,
    desc: i.detail,
    path: i.path,
    cat: c.key as MenuCategory,
  })),
);

/** Rooliehtoiset kohteet: näkyvät Lisää-sivulla mutta EIVÄT alapalkin
 *  valitsimessa (MENU_ITEMS), koska kaikilla ei ole niihin pääsyä. */
const CONDITIONAL_ITEMS: Array<MenuItem & { requires: string }> =
  navigation.conditionalItems.map(i => ({
    icon: i.icon,
    label: i.title,
    desc: i.detail,
    path: i.path,
    cat: i.category as MenuCategory,
    first: i.first === true,
    requires: i.requires,
  }));

export default function MorePage() {
  const navigate = useNavigate();
  const { logout, user } = useAuth();

  // Tutkimus/Koeajot näkyy vain tutkimusoikeuden saaneelle operaattorille
  // (tai manager/admin/ceo:lle). Lipun asettaa admin Käyttäjät-sivulla.
  const showResearch = user?.researchAccess === true || ['manager', 'admin', 'ceo'].includes(user?.role ?? '');
  // 👥 Työnjako (25.8.2026): johdon jakosivu — vain manager/admin/ceo
  // (operaattorilla on oma "Lisää itsellesi" Tänään-sivulla). Kategoriajaossa
  // Suunnittelu ja seuranta -ryhmän kärkeen.
  const showTyonjako = ['manager', 'admin', 'ceo'].includes(user?.role ?? '');
  const allowed: Record<string, boolean> = {
    researchAccess: showResearch,
    management: showTyonjako,
  };
  const menuItems: MenuItem[] = [
    ...MENU_ITEMS,
    ...CONDITIONAL_ITEMS.filter(i => allowed[i.requires] === true),
  ];

  async function handleLogout() {
    await logout();
    navigate('/login', { replace: true });
  }

  return (
    <div>
      <div style={{ textAlign: 'center', marginBottom: 20 }}>
        <h2 style={{ fontSize: TOKENS.font.pageTitle, fontWeight: 800, color: TOKENS.color.textPrimary, margin: 0 }}>Lisää</h2>
        <p style={{ fontSize: TOKENS.font.label, color: TOKENS.color.textSecondary, marginTop: 4 }}>Lisätoiminnot ja työkalut kategorioittain</p>
      </div>

      {CATEGORIES.map(cat => {
        // first-lipulla merkitty (Työnjako) nousee ryhmänsä kärkeen
        const items = menuItems
          .filter(i => i.cat === cat.key)
          .sort((a, b) => Number(b.first ?? false) - Number(a.first ?? false));
        if (items.length === 0) return null;
        return (
          <div key={cat.key} style={{ marginBottom: 22 }}>
            <SectionTitle icon={cat.icon}>{cat.title}</SectionTitle>
            {/* Skaalautuva ruudukko (19.8.2026): puhelimella 1 sarake, leveällä
                näytöllä kortit täyttävät tilan */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 10 }}>
              {items.map(item => (
                <GlassCard key={item.path} onClick={() => navigate(item.path)}
                  style={{ display: 'flex', alignItems: 'center', gap: 14, padding: `${TOKENS.space.contentY}px ${TOKENS.space.contentX}px` }}>
                  <div style={{
                    width: TOKENS.size.menuIcon, height: TOKENS.size.menuIcon, borderRadius: TOKENS.radius.menuIcon,
                    background: `linear-gradient(135deg, ${rgba(TOKENS.color.menuIconTintStrong)}, ${rgba(TOKENS.color.menuIconTintWeak)})`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 24, flexShrink: 0,
                  }}>
                    {item.icon}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: TOKENS.font.title, fontWeight: 700, color: TOKENS.color.textPrimary }}>{item.label}</div>
                    <div style={{ fontSize: TOKENS.font.caption, color: TOKENS.color.textSecondary, marginTop: 2 }}>{item.desc}</div>
                  </div>
                  <span style={{ fontSize: TOKENS.font.title, color: TOKENS.color.textMuted }}>›</span>
                </GlassCard>
              ))}
            </div>
          </div>
        );
      })}

      {/* Kirjaudu ulos — Omat-ryhmän jatkeena sivun lopussa */}
      <div style={{ marginTop: 18 }}>
        <button onClick={handleLogout} style={{
          width: '100%', padding: '14px 24px', background: 'none',
          border: `1px solid ${TOKENS.color.dangerBorder}`, borderRadius: TOKENS.radius.button, color: TOKENS.color.danger,
          fontSize: TOKENS.font.body, fontWeight: 600, cursor: 'pointer', fontFamily: FONT_FAMILY_NARROW,
        }}>
          Kirjaudu ulos
        </button>
      </div>
    </div>
  );
}
